/*
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 **
 **	OCA Color Palette for Mobile App
 */

export const OCAColors = {
  // Primary brand colors - iOS style
  primary: '#007AFF', // iOS blue
  primaryDark: '#0051D0',
  primaryLight: '#4DA2FF',
  
  // Status colors - iOS system colors
  success: '#34C759', // iOS green
  successLight: '#30DB5B',
  warning: '#FF9500', // iOS orange
  warningLight: '#FF9F0A',
  danger: '#FF3B30', // iOS red
  dangerLight: '#FF453A',
  
  // iOS system grays
  gray50: '#F2F2F7',
  gray100: '#E5E5EA',
  gray200: '#D1D1D6',
  gray300: '#C7C7CC',
  gray400: '#AEAEB2',
  gray500: '#8E8E93',
  gray600: '#636366',
  gray700: '#48484A',
  gray800: '#3A3A3C',
  gray900: '#1F2937', // Web app title bar color
  
  // Text colors - iOS style
  textPrimary: '#000000', // True black for iOS
  textSecondary: '#8E8E93', // iOS secondary text
  textTertiary: '#C7C7CC', // iOS tertiary text
  
  // Background colors - iOS style
  background: '#FFFFFF', // Pure white
  backgroundSecondary: '#F2F2F7', // iOS grouped background
  backgroundTertiary: '#E5E5EA', // iOS tertiary background
  
  // Border and separator colors - native iOS  
  separator: 'rgba(60, 60, 67, 0.29)', // Native iOS separator with proper opacity
  opaqueSeparator: '#C6C6C8', // Opaque separator for non-blur contexts
  border: '#C6C6C8', // iOS border
  borderLight: '#E5E5EA',
  
  // Native iOS semantic colors
  label: '#000000', // Primary label color
  secondaryLabel: 'rgba(60, 60, 67, 0.6)', // Secondary label
  tertiaryLabel: 'rgba(60, 60, 67, 0.3)', // Tertiary label
  systemGroupedBackground: '#F2F2F7', // Native grouped background
  
  // Device status colors
  deviceOnline: '#34C759',
  deviceWarning: '#FF9500', 
  deviceAlarm: '#FF3B30',
  deviceOffline: '#8E8E93',
  
  // Alarm status colors
  alarmActive: '#FF3B30',
  alarmAcknowledged: '#FF9500',
  alarmMuted: '#8E8E93',
  alarmResolved: '#34C759',
}

export const Spacing = {
  xs: '0.25rem', // 4px
  sm: '0.5rem',  // 8px
  md: '1rem',    // 16px
  lg: '1.5rem',  // 24px
  xl: '2rem',    // 32px
  xxl: '3rem',   // 48px
}

export const BorderRadius = {
  none: '0',
  sm: '4px',    // Small elements
  default: '8px',  // Default iOS cell radius
  md: '10px',   // Medium elements
  lg: '12px',   // Large elements like sections
  xl: '16px',   // Extra large like sheet corners
  xxl: '20px',  // Very large rounded elements
}

export const Typography = {
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontSize: {
    xs: '0.75rem',   // 12px
    sm: '0.875rem',  // 14px
    base: '1rem',    // 16px
    lg: '1.125rem',  // 18px
    xl: '1.25rem',   // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '1.875rem', // 30px
  },
  fontWeight: {
    extraLight: '200', // Inter Extra Light
    light: '300',    // Inter Light
    normal: '400',   // Inter Regular
    medium: '500',   // Inter Medium
    semibold: '600', // Inter SemiBold
    bold: '700',     // Inter Bold
  },
  lineHeight: {
    tight: '1.25',
    normal: '1.5',
    relaxed: '1.75',
  },
}

// Device Details Panel - Smaller text sizes (1pt smaller)
export const DeviceDetailTypography = {
  fontFamily: Typography.fontFamily,
  fontSize: {
    xs: '0.6875rem', // 11px (was 12px)
    sm: '0.8125rem', // 13px (was 14px) 
    base: '0.9375rem', // 15px (was 16px)
    lg: '1.0625rem', // 17px (was 18px)
    xl: '1.1875rem', // 19px (was 20px)
    '2xl': '1.4375rem', // 23px (was 24px)
  },
  fontWeight: Typography.fontWeight,
  lineHeight: Typography.lineHeight,
}

export const Shadow = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  default: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  button: '0 4px 12px rgba(0, 122, 255, 0.25)', // Primary button shadow
}

// Gradients using OCA colors
export const Gradient = {
  primarySubtle: `linear-gradient(135deg, ${OCAColors.background} 0%, ${OCAColors.gray50} 100%)`,
  primary: `linear-gradient(135deg, ${OCAColors.primary} 0%, ${OCAColors.primaryLight} 100%)`,
  danger: `linear-gradient(135deg, ${OCAColors.danger} 0%, ${OCAColors.dangerLight} 100%)`,
  warning: `linear-gradient(135deg, ${OCAColors.warning} 0%, ${OCAColors.warningLight} 100%)`,
  success: `linear-gradient(135deg, ${OCAColors.success} 0%, ${OCAColors.successLight} 100%)`,
}

// Animation utilities
export const Animation = {
  // Timing functions
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)', // Bouncy spring
  easeOut: 'cubic-bezier(0.4, 0, 0.2, 1)', // Material Design standard
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  easeInOut: 'cubic-bezier(0.4, 0, 0.6, 1)',

  // Durations
  fast: '150ms',
  normal: '250ms',
  slow: '350ms',

  // Transforms
  scaleDown: 'scale(0.96)',
  scaleUp: 'scale(1.02)',
}

// Glassmorphism effects
export const Glass = {
  background: 'rgba(255, 255, 255, 0.85)',
  backgroundDark: 'rgba(255, 255, 255, 0.1)',
  backdrop: 'blur(20px) saturate(180%)',
  border: 'rgba(255, 255, 255, 0.3)',
}